import { ToggleItem } from '@finn/design-system/atoms/toggle';
import { InfoOutlined } from '@finn/design-system/icons/info-outlined';
import { ToggleModule } from '@finn/design-system/modules/ToggleModule';
import {
  getFormattedPrice,
  getPDPDeliveryDateRange,
  getTermLabel,
  useIntl,
} from '@finn/ui-utils';
import get from 'lodash/get';
import { useCallback, useMemo } from 'react';

import { getForBusinessCookie } from '../../../helpers';
import { GenericVehicleDetails } from '../../core';
import { useDiscoveryConfig } from '../../core/data/MetadataProvider';
import { PDPTracking, useTrackingData } from '../../pdp';
import { SwapInfo } from '../../pdp/components/GroupComparison/SwapInfo';
import { useConfigureStore } from '../../pdp-management';
import { OneTimePrice } from '../../product-cards';

export const getTermsPrices = (
  vehicle: GenericVehicleDetails | undefined,
  mode: string,
  isDownPayment: boolean,
  priceType?: string
) => {
  return vehicle?.available_terms.map((term) => {
    const price = get(
      vehicle,
      isDownPayment
        ? `downpayment_prices.available_price_list.${mode}_${term}`
        : `price.available_price_list.${mode}_${term}`,
      0
    );

    return {
      duration: term,
      price: priceType === 'cents' ? price / 100 : price,
    };
  });
};

type ContractDurationProps = {
  className?: string;
};

export const ContractDuration = ({ className }: ContractDurationProps) => {
  const i18n = useIntl();
  const { setTerm, term, downPayment, vehicle } = useConfigureStore();

  const { priceType } = useDiscoveryConfig();
  const isForBusiness = getForBusinessCookie();

  const durations = getTermsPrices(
    vehicle,
    isForBusiness ? 'b2b' : 'b2c',
    downPayment,
    priceType
  );

  const tooltipContent = i18n.formatMessage(
    'checkout.pageData.term_tooltip_content'
  );

  const isDisplayTooltip = durations?.some(
    (duration) => duration.duration === 1
  );

  const flexTermLabel = i18n.formatMessage('checkout.pageData.flex_term_label');

  const contractTermLabel = i18n.formatMessage('pdp.contractTerm');

  const trackingData = useTrackingData();

  const trackDurationChange = useCallback(
    (duration: string) => {
      PDPTracking.contractTermClicked({ ...trackingData, term: duration });
    },
    [trackingData]
  );

  const onChangeDuration = useCallback(
    (oneTimeValue?: OneTimePrice) => {
      trackDurationChange(String(oneTimeValue?.duration));
      setTerm(oneTimeValue?.duration);
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [trackDurationChange]
  );

  const durationsMap = useMemo(
    () =>
      durations?.reduce<Record<string, OneTimePrice>>((acc, duration) => {
        acc[String(duration.duration)] = duration;

        return acc;
      }, {}),
    [durations]
  );

  const availabilityByTerm = vehicle?.availability_by_term[String(term)];
  const date = availabilityByTerm?.available_from ?? vehicle?.available_from;
  const deviationWeek =
    availabilityByTerm?.deviation_in_weeks ??
    vehicle?.available_from_deviation_in_weeks;

  const deliveryText = getPDPDeliveryDateRange({
    date: date || '',
    deviationWeek: deviationWeek || 0,
  });

  return (
    <div className={className}>
      <ToggleModule
        title={contractTermLabel}
        titleAs={'h6'}
        tooltipIcon={<InfoOutlined />}
        tooltipText={isDisplayTooltip ? tooltipContent : ''}
        cols={Number(durations?.length) > 1 ? 2 : 1}
        defaultValue={String(term)}
        value={String(term)}
        onValueChange={(value) => {
          if (value) {
            onChangeDuration(durationsMap?.[value]);
          }
        }}
      >
        {durations?.map((oneTimeValue) => (
          <ToggleItem
            key={oneTimeValue.duration}
            variant="regular"
            title={getTermLabel(oneTimeValue.duration, flexTermLabel, i18n)}
            value={String(oneTimeValue.duration)}
            description={getFormattedPrice(i18n, oneTimeValue.price, 0)}
          />
        ))}
      </ToggleModule>
      <div className="pt-4 body-14-semibold">
        <SwapInfo product={vehicle} term={`${term}`}>
          {deliveryText}
        </SwapInfo>
      </div>
    </div>
  );
};
